import axios from "@util/bdxValidationAxios";

// Custom Validations API (epic LIQ-2142). It lives in the BDX Validation service, so it reuses that
// service's axios instance (base URL + login token). Every error comes back as
// { detail, code, field? } - detail is a sentence that can be shown to the user as-is.
class CustomValidationsService {
	// { enabled, subscription_active, rules: [...], jurisdictions: [...] } - every default rule plus
	// the company's own, and the jurisdictions for the rules table's filter.
	async getCustomValidations(companyId) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations`,
		});
	}

	// Switching off keeps every rule, so switching back on restores them.
	async setEnabled(companyId, enabled) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations/enabled`,
			method: "put",
			data: { enabled },
		});
	}

	// { columns: [{ name, data_type, description }] } - the BDX columns a rule can check (the same for
	// every company).
	async getRuleColumns() {
		return await axios({
			url: "/api/custom-validations/columns",
		});
	}

	// What the rule builder says a column holds - shared by every company, so superusers only. An
	// empty description clears it.
	async setColumnDescription(column, description) {
		return await axios({
			url: `/api/custom-validations/columns/${encodeURIComponent(column)}/description`,
			method: "put",
			data: { description: description || null },
		});
	}

	// { column, events: [{ actor, occurred_at, before, after }] }
	async getColumnHistory(column) {
		return await axios({
			url: `/api/custom-validations/columns/${encodeURIComponent(column)}/history`,
		});
	}

	// What a rule's scope chooses from: { programs: [{ key, umr, year_of_account, jurisdiction, tpa }],
	// jurisdictions: [every jurisdiction], tpas: [the TPAs of those programs] }.
	async getLeadPrograms(companyId) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations/programs`,
		});
	}

	// The rule builder's scope check (LIQ-2716): { programs_in_scope, programs_total, warnings:
	// [{ part, item, message }] } for a scope - { programs_mode, program_umrs, program_key_exceptions,
	// jurisdictions_mode, jurisdictions, tpas_mode, tpas }. Nothing is saved.
	async checkScope(companyId, scope) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations/rules/scope-check`,
			method: "post",
			data: scope,
		});
	}

	// A new custom rule: { name, description, severity, criteria, the scope fields (see checkScope),
	// scope_override, effective_from, effective_to, active }. A scope with choices that can never take
	// effect is refused with code scope_conflict and its warnings, unless scope_override is set.
	async createRule(companyId, rule) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations/rules`,
			method: "post",
			data: rule,
		});
	}

	// Changes a rule that has never checked a BDX file, in place - the same fields as createRule minus
	// active, plus the reason.
	async editRule(companyId, ruleId, rule) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations/rules/${ruleId}`,
			method: "put",
			data: rule,
		});
	}

	// Converts a sentence to a rule (CV-5): { criteria, note } - criteria already checked
	// like a typed rule - or { question } when the sentence isn't clear enough. Nothing is saved.
	// cancelToken (axios.CancelToken.source().token) lets the builder stop waiting.
	async draftRule(companyId, sentence, cancelToken) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations/rules/draft`,
			method: "post",
			data: { sentence },
			cancelToken,
		});
	}

	// Test rule (CV-5): checks a draft rule - { criteria, applicable_keys, jurisdictions } - on the
	// company's latest BDX without saving anything. { file: { id, name, processed_on }, rows_in_file,
	// rows_checked, passed, failed, reasons: { reason: count }, examples: [{ claim_reference, bdx_key,
	// reason, values }], warnings: [...] }
	async testRule(companyId, rule) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations/rules/test`,
			method: "post",
			data: rule,
		});
	}

	// A new version of a rule that has run - the same fields as createRule plus the reason. The old
	// version is deactivated.
	async supersedeRule(companyId, ruleId, rule) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations/rules/${ruleId}/supersede`,
			method: "post",
			data: rule,
		});
	}

	// The rule stops applying from the next validation run; its past results are kept.
	async deactivateRule(companyId, ruleId, reason) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations/rules/${ruleId}/deactivate`,
			method: "post",
			data: { reason: reason || null },
		});
	}

	// Switches a deactivated custom rule back on, from the next validation run.
	async reactivateRule(companyId, ruleId) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations/rules/${ruleId}/reactivate`,
			method: "post",
		});
	}

	// A LimeIQ default rule's severity for this company: higher than its own raises it, its own
	// severity removes the raise.
	async setDefaultSeverity(companyId, ruleId, severity) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations/rules/${ruleId}/severity`,
			method: "put",
			data: { severity },
		});
	}

	// { rule_id, events: [{ rule_id, version, action, actor, occurred_at, reason, changes }] }
	async getRuleHistory(companyId, ruleId) {
		return await axios({
			url: `/api/companies/${companyId}/custom-validations/rules/${ruleId}/history`,
		});
	}
}

// Whether this user may change custom validations - the one place the permission is decided, per
// CV-3 (LIQ-2270). Phase 1: superusers only. CV-8 (LIQ-2275) adds nominated client users here.
export function canManageCustomValidations(user) {
	return !!(user && user.is_superuser);
}

export default new CustomValidationsService();
